<template>
    <v-form ref="form" v-model="valid" :disabled="disabled">
        <!-- TODO [P4] workaround -->
        <v-text-field v-show="false" />
        <v-row>
            <v-col cols="12">
                <span>
                    <span class="text-h6 text-capitalize">
                        {{ `${$t(`transfers.${pointType === 'departure' ? 'from' : 'to'}`)}: ` }}
                    </span>
                    <v-icon class="me-2">mdi-ferry</v-icon>
                    <span class="text-subtitle-2">
                        {{ formTitle }}
                    </span>
                </span>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="!isProductFieldDisabled('POINT_SHIP_CITY')" cols="12" md="4">
                <city-autocomplete
                    v-model="city"
                    :single-line="false"
                    :show-placeholder="false"
                    show-details
                    :label="
                        pointType === 'arrival'
                            ? productFieldLabel('transfers.departure_to_city', 'POINT_SHIP_CITY')
                            : productFieldLabel('transfers.arriving_from_city', 'POINT_SHIP_CITY')
                    "
                    :rules="portCityRules"
                />
            </v-col>
            <v-col v-if="!isProductFieldDisabled('POINT_SHIP_NUMBER')" cols="12" md="2">
                <v-text-field
                    v-model="shipNumber"
                    :label="productFieldLabel('cruises.ship_number', 'POINT_SHIP_NUMBER')"
                    :rules="shipNumberRules"
                />
            </v-col>
            <template v-if="!isProductFieldDisabled('POINT_SHIP_TIME')">
                <v-col cols="12" md="3">
                    <date-picker
                        v-model="date"
                        :label="productFieldLabel('date', 'POINT_SHIP_TIME')"
                        :rules="shipDateRules"
                        :min="minDate"
                        :max="maxDate"
                    />
                </v-col>
                <v-col v-if="!isProductFieldDisabled('POINT_SHIP_TIME')" cols="12" md="3">
                    <time-field
                        v-model="time"
                        :label="productFieldLabel('time', 'POINT_SHIP_TIME')"
                        :rules="shipTimeRules"
                    />
                </v-col>
            </template>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import CityAutocomplete from '@/components/search/forms/CityAutocomplete.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {TransferFormMixin} from '@/components/transfers/mixins/TransferFormMixin.vue'
    import {ProductBookingFieldsMixin} from '@/components/booking/mixins/ProductBookingFieldsMixin.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'

    //TODO Need mixin with TransferAirportForm
    @Component({
        components: {TimeField, CityAutocomplete, DatePicker},
    })
    export class TransferCruisePortForm extends mixins(
        TransferFormMixin,
        ProductBookingFieldsMixin,
        FormValidationRulesMixin
    ) {
        @Prop() pointType
        @Prop() disabled
        @Prop() searchRequest
        @VModel({default: false}) valid
        @Prop() offer

        city = null

        port = null

        @Watch('city', {immediate: false})
        onCityChange(city) {
            this.cityId = city?.id
        }

        async mounted() {
            await this.initData()
        }

        async initData() {
            const date = this.$dateFns.parseISO(this.date)
            if (this.isReturnTransfer) {
                this.setProp('dateTime', this.offer.date + 'T00:00')
            } else if (
                !this.date ||
                this.$dateFns.isAfter(date, this.$dateFns.parseISO(this.maxDate)) ||
                this.$dateFns.isBefore(date, this.$dateFns.parseISO(this.minDate))
            ) {
                this.setProp('dateTime', this.searchRequest.dateTime + 'T00:00')
            }
            if (this.cityId) {
                try {
                    const {
                        cities: [city],
                    } = await this.$api.locations.get({id: this.cityId, limit: 1})
                    this.city = city
                } catch (e) {
                    this.cityId = null
                }
            }
        }

        splitDateTime() {
            let dateTime = this.getProp('dateTime')
            if (dateTime) {
                dateTime = dateTime.split('T')
            } else {
                dateTime = []
            }
            return dateTime
        }

        get portCityRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_SHIP_CITY') && !this.disableRequiredRule) {
                rules.push(
                    this.pointType === 'arrival'
                        ? this.requiredRule('transfers.departure_to_city', 'POINT_SHIP_CITY')
                        : this.requiredRule('transfers.arriving_from_city', 'POINT_SHIP_CITY')
                )
            }
            return rules
        }

        get shipNumberRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_SHIP_NUMBER') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('cruises.ship_number', 'POINT_SHIP_NUMBER'))
            }
            return rules
        }

        get shipDateRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_SHIP_TIME') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('date', 'POINT_SHIP_TIME'))
            }
            return rules
        }

        get shipTimeRules() {
            const rules = []
            if (this.isProductFieldMandatory('POINT_SHIP_TIME') && !this.disableRequiredRule) {
                rules.push(this.requiredRule('time', 'POINT_SHIP_TIME'))
            }
            return rules
        }

        get shipNumber() {
            return this.getProp('shipNumber')
        }

        set shipNumber(val) {
            this.setProp('shipNumber', val)
        }

        get date() {
            const dateTime = this.getProp('dateTime')
            if (!dateTime) return dateTime
            return dateTime.split('T')[0]
        }

        set date(val) {
            const dateTime = this.splitDateTime()
            dateTime[0] = val
            this.setProp('dateTime', dateTime.join('T'))
        }

        get time() {
            const dateTime = this.getProp('dateTime')
            if (!dateTime) return dateTime
            return dateTime.split('T')[1]
        }

        set time(val) {
            const dateTime = this.splitDateTime()
            dateTime[1] = val
            this.setProp('dateTime', dateTime.join('T'))
        }

        get cityId() {
            return this.getProp('cityId')
        }

        set cityId(val) {
            this.setProp('cityId', val)
        }

        get minDate() {
            if (this.pointType === 'arrival') {
                return this.searchRequest.dateTime
            } else {
                return this.$dateFns.format(
                    this.$dateFns.subDays(this.$dateFns.parseISO(this.searchRequest.dateTime), 1)
                )
            }
        }

        get maxDate() {
            if (this.isReturnTransfer) {
                return this.$dateFns.format(this.$dateFns.addDays(this.$dateFns.parseISO(this.offer.date), 1))
            }
            if (this.pointType === 'arrival') {
                return this.$dateFns.format(
                    this.$dateFns.addDays(this.$dateFns.parseISO(this.searchRequest.dateTime), 1)
                )
            } else {
                return this.searchRequest.dateTime
            }
        }
    }

    export default TransferCruisePortForm
</script>
